import type { MarketplaceEntry } from "../types/index";

interface Props {
  plugin: MarketplaceEntry;
}

export default function InstallInClaude({ plugin }: Props) {
  const hasConnectors = (plugin.mcpServers?.length ?? 0) > 0;

  // The CAI marketplace is pre-installed org-wide via Claude's enterprise admin
  // tooling, so there's no "add the marketplace" step — plug-ins appear directly
  // under Organization Plugins. Standalone skills (unpacked
  // from a plugin) are NOT published to Organization Plugins, so the only way to use
  // one in Chat/Cowork is to download it and add it manually (the detail page
  // renders that download section separately). extensionType may be absent on
  // legacy records; the strict === "skill" check treats anything else (including
  // undefined) as a plug-in.
  const isSkill = plugin.extensionType === "skill";
  const noun = isSkill ? "skill" : "plug-in";

  return (
    <div style={{ marginBottom: "1.25rem", border: "1px solid var(--color-border-medium)", borderRadius: "8px", padding: "0.85rem 1rem", backgroundColor: "var(--color-surface)" }}>
      <div style={{ fontSize: "0.95rem", fontWeight: 700, color: "var(--color-heading)", marginBottom: "0.5rem" }}>
        Install in Claude (Chat &amp; Cowork)
      </div>

      {isSkill ? (
        <div style={{ fontSize: "0.85rem", lineHeight: "1.6", color: "var(--color-text-primary)" }}>
          <strong>{plugin.pluginName}</strong> is an individual skill, not a plug-in, so it isn&rsquo;t
          listed in Organization Plugins in Claude Desktop. To use it in Chat or Cowork, download it below and add it
          manually.
        </div>
      ) : (
        <>
          <ol style={{ margin: "0.1rem 0 0", paddingLeft: "1.25rem", fontSize: "0.85rem", lineHeight: "1.6", color: "var(--color-text-primary)" }}>
            <li>In Claude, go to <strong>Customize → Organization Plugins → + Icon</strong></li>
            <li>Search for <strong>{plugin.pluginName}</strong></li>
            <li>Click <strong>+</strong> to install it — the plug-in is available in your next conversation.</li>
          </ol>
        </>
      )}

      <div style={{ fontSize: "0.85rem", lineHeight: "1.6", marginTop: "0.6rem" }}>
        <a href="https://coxautoinc.sharepoint.com/sites/service-engineering-enablement/delivery-platform/SitePages/Installing-Plugins-and-Skills.aspx" target="_blank" rel="noopener noreferrer" style={{ color: "var(--color-brand)" }} aria-label="Video: Installing plugins and skills in Claude Desktop (opens in new window)">
          Video: Installing plugins and skills in Claude Desktop
        </a>
      </div>

      {hasConnectors && (
        <div
          role="note"
          style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", marginTop: "0.75rem", backgroundColor: "var(--color-warning-bg)", border: "1px solid var(--color-warning-border)", borderRadius: "8px", padding: "0.6rem 0.75rem", fontSize: "0.8rem", lineHeight: "1.5", color: "var(--color-warning-text)" }}
        >
          <svg aria-hidden="true" focusable="false" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--color-warning-text)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: "0.1rem" }}>
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>
            This {noun} bundles an MCP connector. In Chat and Cowork, connectors route through
            Anthropic&rsquo;s cloud, so one targeting an internal-only server won&rsquo;t reach it there —
            even though the {noun} installs. It works in Claude Code, which runs locally.
          </span>
        </div>
      )}
    </div>
  );
}
